/**
 * `last_updated` — `LastUpdatedValue` (`specs/M2-tier1-column-types.md` §5.10, `[09§A.7]`).
 *
 * *"read-only … automatically set by the system whenever any column value on the item
 * changes."* It is **never empty** (908/908 populated) and needs **no `cell_values` row**: it is
 * projected from `items.updated_at` + `items.updated_by` (M2 §4). `updater_id` is null when the
 * last change came from an automation.
 *
 * `toText` is `"YYYY-MM-DD HH:MM:SS UTC"` — the literal `UTC` suffix, never localised
 * (`0077-change-multiple-column-values.json`: `2026-09-09 17:45:34 UTC`).
 */
import { z } from 'zod';
import { DEFAULT_SQL_REFS, defaultFilterSql } from '../sql.js';
import { formatUtcStamp, parseInstant, textShadow, toIsoSeconds } from '../helpers.js';
import { ColumnValueError, defineColumnType } from '../types.js';
export const lastUpdatedSettingsSchema = z.looseObject({
    /** `personOnly | dateOnly | personAndDate` by analogy with `creation_log` — ASSUMED (Q72). */
    mode: z.string().optional(),
    dateFormatMode: z.enum(['dateAgo', 'dateFormat']).optional(),
    hide_footer: z.boolean().optional(),
});
import { lastUpdatedFilterConditions } from './filters.js';
import { operatorsOf } from '../filter-conditions.js';
export const lastUpdatedValueSchema = z.looseObject({
    updated_at: z.string(),
    updater_id: z.string().nullable().optional(),
});
function coerce(raw) {
    if (raw === null || raw === undefined)
        return null;
    if (typeof raw !== 'object') {
        throw new ColumnValueError('last_updated', `expected an object, received ${typeof raw}`);
    }
    const record = raw;
    const instant = parseInstant(record.updated_at);
    if (instant === null)
        return null;
    const updater = record.updater_id ?? record.updated_by ?? null;
    return {
        updated_at: toIsoSeconds(instant),
        updater_id: updater === null || updater === undefined ? null : String(updater),
    };
}
function renderStamp(iso) {
    const instant = parseInstant(iso ?? null);
    return instant === null ? '' : formatUtcStamp(instant);
}
export const lastUpdatedColumn = defineColumnType({
    type: 'last_updated',
    title: 'Last Updated',
    description: 'When the item last changed, and who changed it. Read-only.',
    apiFragmentName: 'LastUpdatedValue',
    legacyTypeName: 'pulse-updated',
    idPrefix: 'last_updated',
    valueSchema: lastUpdatedValueSchema,
    settingsSchema: lastUpdatedSettingsSchema,
    defaultSettings: {},
    settingsStrSchema: lastUpdatedSettingsSchema,
    writable: false,
    supportsSimpleValue: false,
    clearForms: [],
    /** Never `is_empty`: the column is never empty (M2 §5.10). */
    filterConditions: lastUpdatedFilterConditions,
    filterOperators: operatorsOf(lastUpdatedFilterConditions),
    aggregations: ['latest', 'earliest', 'range', 'count', 'none'],
    emptyText: '',
    /**
     * 135: board `717364248` `Last Updated` stores `width: null` and monday renders it at 135
     * (P-022, re-measured by T-8a).
     */
    defaultWidth: 135,
    computed: {
        dependsOn() {
            return [{ kind: 'item_meta', field: 'updated_at' }];
        },
        compute(ctx) {
            const instant = parseInstant(ctx.item?.updatedAt ?? null);
            if (instant === null)
                return null;
            const updater = ctx.item?.updatedBy ?? null;
            return {
                updated_at: toIsoSeconds(instant),
                updater_id: updater === null ? null : String(updater),
            };
        },
        /** No stored row: projected from the item on read (M2 §4). */
        materialise: 'never',
        api: { value: 'stored', text: 'stored' },
    },
    parseValue(raw) {
        if (raw === null || raw === undefined)
            return null;
        throw new ColumnValueError('last_updated', 'the last updated column is read-only');
    },
    toText(value) {
        return value === null ? '' : renderStamp(value.updated_at);
    },
    toSortKeys(value) {
        if (value === null)
            return { text: null, number: null, date: null };
        const instant = parseInstant(value.updated_at);
        if (instant === null)
            return { text: null, number: null, date: null };
        return { text: textShadow(formatUtcStamp(instant)), number: instant.getTime(), date: instant };
    },
    isEqual(a, b) {
        return ((a?.updated_at ?? null) === (b?.updated_at ?? null) &&
            (a?.updater_id ?? null) === (b?.updater_id ?? null));
    },
    toActivityValue(value) {
        return value;
    },
    fromMondayValue(raw) {
        return coerce(raw);
    },
    toMondayValue(value) {
        return value === null ? null : { ...value };
    },
    toSimpleValue() {
        // `change_simple_column_value` is not supported for a read-only system column.
        return null;
    },
    fromMondaySettings(raw) {
        return lastUpdatedSettingsSchema.parse(raw ?? {});
    },
    toMondaySettings(settings) {
        return { ...settings };
    },
    toMondaySettingsStr(settings) {
        return { ...settings };
    },
    toApiFragment(value) {
        if (value === null)
            return {};
        return { updated_at: value.updated_at, updater_id: value.updater_id ?? null };
    },
    /**
     * No webhook recording for this type: the conservative pass-through is monday's own `value`
     * object `[whk§7]`, which is what `toMondayValue` already produces (q330).
     */
    toWebhookValue(value, settings) {
        return this.toMondayValue(value, settings);
    },
    bucketsOf(value) {
        return value?.updated_at ? [{ key: value.updated_at.slice(0, 10), label: value.updated_at.slice(0, 10) }] : [];
    },
    /** The same two halves as `creation_log`, over the updater and the update date. */
    compareAttributes: {
        person: {
            shadow: 'text',
            sql: (_cell, refs) => `${refs.item}.updated_by::text`,
            keys: (value) => ({ text: value?.updater_id ?? null, number: null, date: null }),
        },
        date: {
            shadow: 'date',
            keys: (value) => {
                const instant = value === null ? null : parseInstant(value.updated_at);
                return instant === null
                    ? { text: null, number: null, date: null }
                    : { text: null, number: instant.getTime(), date: instant };
            },
        },
    },
    /** Projected like `creation_log`: the SQL reads `items.updated_at`, never a shadow (T-9p). */
    filterSql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        return defaultFilterSql('date', ctx, `${refs.item}.updated_at`);
    },
    sortSql(refs) {
        return [`${refs.item}.updated_at`];
    },
    summarySql(ctx) {
        const refs = ctx.refs ?? DEFAULT_SQL_REFS;
        const date = `${refs.item}.updated_at`;
        switch (ctx.aggregation) {
            case 'earliest':
                return { text: `min(${date})`, params: [], shape: 'scalar' };
            case 'latest':
                return { text: `max(${date})`, params: [], shape: 'scalar' };
            case 'range':
                return { text: `min(${date}), max(${date})`, params: [], shape: 'pair' };
            case 'count':
                return { text: `count(${refs.item}.id)`, params: [], shape: 'scalar' };
            default:
                return null;
        }
    },
});
//# sourceMappingURL=index.js.map